import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Bell, CalendarClock, Download, MapPin } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function OrderCalendar() {
  const { data: events = [] } = useQuery({ queryKey: ["admincalendar"], queryFn: async () => (await api.get("/admin/calendar")).data });
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  const { due, rem, pick } = useMemo(() => {
    const due = {}, rem = {}, pick = {};
    for (const e of events) {
      if (e.fulfillment_date) (due[e.fulfillment_date] ||= []).push(e);
      if (e.reminder_date) (rem[e.reminder_date] ||= []).push(e);
      if (e.pickup_date) (pick[e.pickup_date] ||= []).push(e);
    }
    return { due, rem, pick };
  }, [events]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const weekList = useMemo(() => {
    const today = new Date(todayStr + "T00:00:00");
    const end = new Date(today.getTime() + 7 * 86400000);
    return events
      .filter((e) => e.fulfillment_date && new Date(e.fulfillment_date + "T00:00:00") >= today && new Date(e.fulfillment_date + "T00:00:00") <= end)
      .sort((a, b) => a.fulfillment_date.localeCompare(b.fulfillment_date));
  }, [events, todayStr]);
  const pickupWeek = useMemo(() => {
    const today = new Date(todayStr + "T00:00:00");
    const end = new Date(today.getTime() + 7 * 86400000);
    return events
      .filter((e) => e.pickup_date && !e.collected && new Date(e.pickup_date + "T00:00:00") >= today && new Date(e.pickup_date + "T00:00:00") <= end)
      .sort((a, b) => a.pickup_date.localeCompare(b.pickup_date) || a.pickup_time.localeCompare(b.pickup_time));
  }, [events, todayStr]);

  const startDow = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const iso = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const prev = () => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const next = () => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const fmt = (s) => new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-6" data-testid="admin-calendar">
      <div className="lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold text-white">{MONTHS[cursor.m]} {cursor.y}</h3>
          <div className="flex gap-2">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/calendar.ics`} data-testid="cal-export-ics" title="Export all fulfillment dates and booked pickups (.ics)" className="h-9 inline-flex items-center gap-1.5 rounded-full bg-[#1e222d] border border-zinc-800 px-3 text-xs font-semibold text-white hover:border-[#22c3d6]"><Download size={14} />Export .ics</a>
            <button onClick={prev} data-testid="cal-prev" className="w-9 h-9 rounded-full bg-[#1e222d] border border-zinc-800 flex items-center justify-center text-white hover:border-[#ff6a13]"><ChevronLeft size={16} /></button>
            <button onClick={next} data-testid="cal-next" className="w-9 h-9 rounded-full bg-[#1e222d] border border-zinc-800 flex items-center justify-center text-white hover:border-[#ff6a13]"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-zinc-400 mb-2">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#ff6a13]" />Due (fulfillment)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#10b981]" />Pickup booked</span>
          <span className="flex items-center gap-1.5"><Bell size={12} className="text-[#ffd600]" />48-hr reminder</span>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {DOW.map((d) => <div key={d} className="text-center text-[11px] text-zinc-500 font-mono-p py-1">{d}</div>)}
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} className="min-h-[84px]" />;
            const day = iso(d);
            const dueList = due[day] || [];
            const remList = rem[day] || [];
            const pickList = pick[day] || [];
            const isToday = day === todayStr;
            return (
              <div key={day} data-testid={`cal-day-${day}`} className={`min-h-[84px] rounded-lg border p-1.5 bg-[#12141a] ${isToday ? "border-[#22c3d6]" : "border-zinc-800"}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-mono-p ${isToday ? "text-[#22c3d6]" : "text-zinc-400"}`}>{d}</span>
                  {remList.length > 0 && <Bell size={11} className="text-[#ffd600]" />}
                </div>
                <div className="mt-1 space-y-0.5">
                  {dueList.slice(0, 3).map((e) => (
                    <div key={e.order_id} title={`${e.order_number} · ${e.customer_name}`} className="text-[10px] bg-[#ff6a13]/15 border border-[#ff6a13]/40 rounded px-1 py-0.5 truncate text-white">{e.order_number}</div>
                  ))}
                  {dueList.length > 3 && <div className="text-[10px] text-zinc-500">+{dueList.length - 3} more</div>}
                  {pickList.slice(0, 2).map((e) => (
                    <div key={`p${e.order_id}`} data-testid={`cal-pickup-${e.order_number}`} title={`Pickup ${e.pickup_time} · ${e.customer_name} · ${e.pickup_location || ""}`} className={`text-[10px] bg-[#10b981]/15 border border-[#10b981]/40 rounded px-1 py-0.5 truncate text-white ${e.collected ? "opacity-50 line-through" : ""}`}>{e.pickup_time} {e.customer_name?.split(" ")[0]}</div>
                  ))}
                  {pickList.length > 2 && <div className="text-[10px] text-[#10b981]">+{pickList.length - 2} pickups</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-1" data-testid="due-this-week">
        <h3 className="font-display text-lg font-bold text-white flex items-center gap-2 mb-3"><CalendarClock size={17} className="text-[#22c3d6]" />Due this week</h3>
        <div className="space-y-2">
          {weekList.length === 0 && <p className="text-sm text-zinc-500 font-mono-p" data-testid="due-week-empty">Nothing due in the next 7 days.</p>}
          {weekList.map((e) => {
            const urgent = e.reminder_date && e.reminder_date <= todayStr;
            const isPaid = e.payment_status === "paid";
            return (
              <div key={e.order_id} data-testid={`due-row-${e.order_number}`} className={`rounded-xl border p-3 ${urgent ? "border-[#ffd600]/50 bg-[#ffd600]/5" : "border-zinc-800 bg-[#12141a]"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono-p text-sm text-white">{e.order_number}</span>
                  {urgent && <span className="flex items-center gap-1 text-[10px] text-[#ffd600] font-semibold"><Bell size={11} />48hr</span>}
                </div>
                <p className="text-xs text-zinc-400 mt-0.5 truncate">{e.customer_name}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-[#22c3d6] font-mono-p">{fmt(e.fulfillment_date)}</span>
                  <span className={`text-[10px] font-mono-p px-1.5 py-0.5 rounded-full border ${isPaid ? "text-[#10b981] border-[#10b981]/40" : "text-[#ffd600] border-[#ffd600]/40"}`}>{isPaid ? "paid" : "awaiting deposit"}</span>
                </div>
              </div>
            );
          })}
        </div>
        <h3 className="font-display text-lg font-bold text-white flex items-center gap-2 mb-3 mt-6"><MapPin size={17} className="text-[#10b981]" />Pickups this week</h3>
        <div className="space-y-2" data-testid="pickups-this-week">
          {pickupWeek.length === 0 && <p className="text-sm text-zinc-500 font-mono-p" data-testid="pickups-week-empty">No pickups booked in the next 7 days.</p>}
          {pickupWeek.map((e) => (
            <div key={`pw${e.order_id}`} data-testid={`pickup-row-${e.order_number}`} className="rounded-xl border border-[#10b981]/30 bg-[#12141a] p-3">
              <div className="flex items-center justify-between">
                <span className="font-mono-p text-sm text-white">{e.order_number}</span>
                <span className="text-xs text-[#10b981] font-mono-p font-semibold">{fmt(e.pickup_date)} · {e.pickup_time}</span>
              </div>
              <p className="text-xs text-zinc-400 mt-0.5 truncate">{e.customer_name}{e.pickup_location ? ` · ${e.pickup_location}` : ""}</p>
              {e.balance_owing > 0 && <p className="text-[10px] text-[#ff6a13] font-mono-p mt-1">${e.balance_owing.toFixed(2)} due before pickup</p>}
            </div>
          ))}
        </div>
      </div>
      {events.length === 0 && <p className="text-zinc-500 font-mono-p lg:col-span-3" data-testid="calendar-empty">No orders have a requested fulfillment date yet.</p>}
    </div>
  );
}
